import type { EventDefinition } from "./catalog";
import type { HookEventName } from "./index";
import { HOOK_EVENTS } from "./names";

/**
 * Exemplos de conteúdo, um por evento.
 *
 * Quem cadastra um destino precisa saber o que vai receber ANTES de escrever
 * código do outro lado. Sem isso, a única forma de descobrir o formato é
 * cadastrar, esperar acontecer, e ler o que chegou — e enquanto isso o
 * integrador está adivinhando.
 *
 * Os payloads são interfaces TypeScript: existem em tempo de compilação e
 * somem no runtime, então não há como derivar exemplo delas. A saída é
 * declarar — mas declarando as ENTIDADES uma vez e compondo os eventos a
 * partir delas, senão viram 45 blocos de JSON que envelhecem cada um por
 * conta própria.
 *
 * Os valores são fictícios de propósito (número 5511999999999, "Maria
 * Silva"): é exemplo de formato, não dado de ninguém.
 */

const CONTATO = {
  id: 42,
  name: "Maria Silva",
  number: "5511999999999",
  email: "maria@exemplo.com.br",
  isGroup: false,
  companyId: 1
};

const TICKET = {
  id: 1234,
  uuid: "a1b2c3d4-0000-0000-0000-000000000000",
  status: "open",
  isGroup: false,
  channel: "whatsapp",
  unreadMessages: 0,
  contactId: CONTATO.id,
  userId: 7,
  queueId: 3,
  whatsappId: 2,
  companyId: 1,
  isBot: false,
  contact: CONTATO,
  createdAt: "2026-08-04T14:29:55.000Z",
  updatedAt: "2026-08-04T14:30:00.000Z"
};

const MENSAGEM = {
  id: 98765,
  wid: "3EB0C767D26B8A3F1B2C",
  body: "Boa tarde, preciso de ajuda com meu pedido",
  fromMe: false,
  source: "contact",
  ticketId: TICKET.id,
  contactId: CONTATO.id,
  companyId: 1,
  mediaType: "chat",
  mediaUrl: null,
  createdAt: "2026-08-04T14:30:00.000Z"
};

const CONEXAO = {
  id: 2,
  name: "Comercial",
  channel: "whatsapp",
  status: "CONNECTED",
  number: "5511988887777",
  isDefault: true,
  companyId: 1
};

const USUARIO = {
  id: 7,
  name: "João Atendente",
  email: "joao@empresa.com.br",
  profile: "user",
  online: true,
  companyId: 1
};

const TAG = { id: 5, name: "Urgente", color: "#e74c3c", companyId: 1 };

const EMPRESA = {
  id: 88,
  name: "Empresa Nova Ltda",
  email: "contato@empresanova.com.br",
  status: true,
  serverId: "app.exemplo.com.br",
  createdAt: "2026-08-04T14:30:00.000Z"
};

/** Exemplo por evento. O que não estiver aqui cai no genérico da tela. */
/**
 * Chave tipada pelo union de nomes: `Record<string, ...>` aceitava qualquer
 * coisa, e um exemplo com o nome errado nunca seria encontrado pelo `sampleOf`
 * — a tela mostraria o evento sem exemplo, sem erro nenhum. `Partial` porque
 * evento novo pode entrar antes do exemplo (o teste de cobertura cobra).
 */
const PLUGIN_INSTALACAO = {
  pluginId: "notificamehub",
  channel: "notificamehub",
  baseUrl: "https://plugins.exemplo.com.br/1",
  displayName: "NotificaMe Hub",
  version: "2.0.0",
  serverId: "app.exemplo.com.br"
};

export const EVENT_SAMPLES: Partial<Record<HookEventName, Record<string, any>>> = {
  // ─── Atendimento ───
  [HOOK_EVENTS.ticket.CREATED]: { ticket: TICKET, companyId: 1 },
  [HOOK_EVENTS.ticket.UPDATED]: {
    ticket: TICKET,
    companyId: 1,
    changes: { status: { from: "pending", to: "open" } }
  },
  [HOOK_EVENTS.ticket.ASSIGNED]: {
    ticket: TICKET,
    companyId: 1,
    userId: USUARIO.id,
    oldUserId: null
  },
  [HOOK_EVENTS.ticket.TRANSFERRED]: {
    ticket: TICKET,
    companyId: 1,
    oldQueueId: 1,
    newQueueId: 3,
    oldUserId: 4,
    newUserId: 7
  },
  [HOOK_EVENTS.ticket.CLOSED]: {
    ticket: { ...TICKET, status: "closed" },
    companyId: 1,
    oldStatus: "open"
  },
  [HOOK_EVENTS.ticket.REOPENED]: { ticket: TICKET, companyId: 1, oldStatus: "closed" },
  [HOOK_EVENTS.ticket.DELETED]: { ticket: TICKET, companyId: 1 },
  [HOOK_EVENTS.ticket.TAGGED]: { ticket: TICKET, tag: TAG, companyId: 1 },
  [HOOK_EVENTS.ticket.UNTAGGED]: { ticket: TICKET, tag: TAG, companyId: 1 },

  // ─── Mensagem ───
  [HOOK_EVENTS.ticket.MESSAGE_RECEIVED]: {
    message: MENSAGEM,
    ticket: TICKET,
    contact: CONTATO,
    companyId: 1
  },
  [HOOK_EVENTS.ticket.MESSAGE_SENT]: {
    message: {
      ...MENSAGEM,
      id: 98766,
      body: "Claro! Já verifico para você.",
      fromMe: true,
      source: "agent",
      sourceId: USUARIO.id
    },
    ticket: TICKET,
    companyId: 1
  },
  [HOOK_EVENTS.ticket.MESSAGE_EDITED]: {
    message: { ...MENSAGEM, body: "Boa tarde, preciso de ajuda (corrigido)" },
    ticket: TICKET,
    companyId: 1,
    oldBody: "Boa tarde, preciso de ajuda com meu pedido"
  },
  [HOOK_EVENTS.ticket.MESSAGE_DELETED]: { message: MENSAGEM, ticket: TICKET, companyId: 1 },
  [HOOK_EVENTS.ticket.RATED]: { ticket: TICKET, rate: 5, userId: 1, companyId: 1 },
  [HOOK_EVENTS.ticket.MESSAGE_REACTED]: {
    message: MENSAGEM,
    ticket: TICKET,
    companyId: 1,
    reaction: "👍"
  },
  [HOOK_EVENTS.message.SENT]: { message: { ...MENSAGEM, fromMe: true }, companyId: 1 },
  [HOOK_EVENTS.message.EDITED]: { message: MENSAGEM, companyId: 1 },
  [HOOK_EVENTS.message.DELETED]: { message: MENSAGEM, companyId: 1 },
  [HOOK_EVENTS.message.REACTED]: { message: MENSAGEM, companyId: 1, reaction: "👍" },
  [HOOK_EVENTS.message.ACK_CHANGED]: {
    message: MENSAGEM,
    companyId: 1,
    ack: 3,
    previousAck: 2
  },
  [HOOK_EVENTS.message.MEDIA_RETRIED]: {
    message: { ...MENSAGEM, mediaType: "image", mediaUrl: "/public/arquivo.jpg" },
    companyId: 1
  },
  [HOOK_EVENTS.message.RECEIVED]: { message: MENSAGEM, companyId: 1 },
  [HOOK_EVENTS.message.FAILED]: { message: MENSAGEM, ticket: TICKET, contact: CONTATO, erro: "conexão desconectada", companyId: 1 },

  // ─── Nota interna ───
  [HOOK_EVENTS.note.CREATED]: {
    note: { ...MENSAGEM, fromMe: true, isPrivate: true, body: "Cliente pediu retorno na sexta" },
    ticket: TICKET,
    contact: CONTATO,
    origin: "agent",
    userId: 1,
    companyId: 1
  },
  [HOOK_EVENTS.note.EDITED]: {
    note: { ...MENSAGEM, fromMe: true, isPrivate: true, body: "Retorno mudou para segunda" },
    ticket: TICKET,
    contact: CONTATO,
    oldBody: "Cliente pediu retorno na sexta",
    newBody: "Retorno mudou para segunda",
    userId: 1,
    companyId: 1
  },
  [HOOK_EVENTS.note.DELETED]: {
    note: { ...MENSAGEM, fromMe: true, isPrivate: true, body: "Cliente pediu retorno na sexta" },
    ticket: TICKET,
    contact: CONTATO,
    userId: 1,
    companyId: 1
  },

  // ─── Campanha ───
  [HOOK_EVENTS.campaign.STARTED]: {
    campaign: { id: 7, name: "Promoção de agosto", status: "EM_ANDAMENTO", confirmation: false },
    total: 1500,
    companyId: 1
  },
  [HOOK_EVENTS.campaign.FINISHED]: {
    campaign: { id: 7, name: "Promoção de agosto", status: "FINALIZADA", confirmation: false },
    progresso: { total: 1500, enviadas: 1487, falhas: 13 },
    companyId: 1
  },
  [HOOK_EVENTS.campaign.CANCELLED]: {
    campaign: { id: 7, name: "Promoção de agosto", status: "CANCELADA", confirmation: false },
    progresso: { total: 1500, enviadas: 320, falhas: 2 },
    userId: 1,
    companyId: 1
  },
  [HOOK_EVENTS.campaign.MESSAGE_SENT]: {
    campaign: { id: 7, name: "Promoção de agosto", status: "EM_ANDAMENTO", confirmation: false },
    contact: CONTATO,
    shippingId: 991,
    body: "Oi Fulano, temos novidade para você!",
    companyId: 1
  },
  [HOOK_EVENTS.campaign.MESSAGE_FAILED]: {
    campaign: { id: 7, name: "Promoção de agosto", status: "EM_ANDAMENTO", confirmation: false },
    contact: CONTATO,
    shippingId: 992,
    erro: "número não existe no WhatsApp",
    companyId: 1
  },
  [HOOK_EVENTS.campaign.CONFIRMATION_REQUESTED]: {
    campaign: { id: 8, name: "Convite", status: "EM_ANDAMENTO", confirmation: true },
    contact: CONTATO,
    shippingId: 1001,
    body: "Podemos te enviar o convite? Responda SIM",
    companyId: 1
  },
  [HOOK_EVENTS.campaign.CONFIRMED]: {
    campaign: { id: 8, name: "Convite", status: "FINALIZADA", confirmation: true },
    contact: CONTATO,
    shippingId: 1001,
    resposta: "sim",
    companyId: 1
  },

  // ─── Ligação ───
  [HOOK_EVENTS.call.RECEIVED]: {
    call: { id: 55, externalId: "CALL123", isVideo: false, at: "2026-08-19T14:05:00.000Z" },
    contact: CONTATO,
    connectionId: 72,
    companyId: 1
  },
  [HOOK_EVENTS.call.REJECTED]: {
    call: { id: 56, externalId: "CALL124", isVideo: true, at: "2026-08-19T14:06:00.000Z" },
    contact: CONTATO,
    connectionId: 72,
    mensagemEnviada: "Não atendemos ligações por aqui, mande uma mensagem.",
    companyId: 1
  },

  // ─── Agendamento ───
  [HOOK_EVENTS.schedule.CREATED]: {
    schedule: { id: 42, body: "Lembrete da consulta", sendAt: "2026-08-20T13:00:00.000Z", status: "PENDENTE" },
    contact: CONTATO,
    userId: 1,
    companyId: 1
  },
  [HOOK_EVENTS.schedule.SENT]: {
    schedule: { id: 42, body: "Lembrete da consulta", sendAt: "2026-08-20T13:00:00.000Z", status: "ENVIADA" },
    contact: CONTATO,
    ticket: TICKET,
    enviadoAoContato: true,
    companyId: 1
  },
  [HOOK_EVENTS.schedule.FAILED]: {
    schedule: { id: 43, body: "Lembrete", sendAt: "2026-08-20T13:00:00.000Z", status: "ERRO" },
    contact: CONTATO,
    erro: "conexão desconectada",
    companyId: 1
  },

  // ─── Contato ───
  [HOOK_EVENTS.contact.CREATED]: { contact: CONTATO, companyId: 1 },
  [HOOK_EVENTS.contact.UPDATED]: {
    contact: CONTATO,
    companyId: 1,
    changes: { name: { from: "Maria", to: "Maria Silva" } }
  },
  [HOOK_EVENTS.contact.DELETED]: { contact: CONTATO, companyId: 1 },
  [HOOK_EVENTS.contact.TAGGED]: { contact: CONTATO, tag: TAG, companyId: 1 },
  [HOOK_EVENTS.contact.BLOCKED]: { contact: CONTATO, companyId: 1, connectionId: 7 },
  [HOOK_EVENTS.contact.UNBLOCKED]: { contact: CONTATO, companyId: 1, connectionId: 7 },
  [HOOK_EVENTS.contact.UNTAGGED]: { contact: CONTATO, tag: TAG, companyId: 1 },

  // ─── Conexão ───
  [HOOK_EVENTS.connection.CREATED]: { connection: CONEXAO, companyId: 1 },
  [HOOK_EVENTS.connection.UPDATED]: { connection: CONEXAO, companyId: 1 },
  [HOOK_EVENTS.connection.DELETED]: { connection: CONEXAO, companyId: 1 },
  [HOOK_EVENTS.connection.STATUS_CHANGED]: {
    connection: CONEXAO,
    companyId: 1,
    status: "CONNECTED",
    previousStatus: "OPENING"
  },
  [HOOK_EVENTS.connection.DISCONNECTED]: {
    connection: { ...CONEXAO, status: "DISCONNECTED" },
    companyId: 1,
    reason: "logged_out"
  },
  [HOOK_EVENTS.connection.QRCODE_RECEIVED]: {
    connection: { ...CONEXAO, status: "qrcode" },
    companyId: 1,
    qrcode: "2@A1b2C3d4E5f6..."
  },
  [HOOK_EVENTS.connection.RATE_LIMITED]: { connection: CONEXAO, companyId: 1, retryAfter: 30 },

  // ─── Usuário ───
  [HOOK_EVENTS.user.CREATED]: { user: USUARIO, companyId: 1 },
  [HOOK_EVENTS.user.UPDATED]: {
    user: USUARIO,
    companyId: 1,
    changes: { profile: { from: "user", to: "admin" } }
  },
  [HOOK_EVENTS.user.DELETED]: { user: USUARIO, companyId: 1 },
  [HOOK_EVENTS.user.ONLINE]: { user: USUARIO, at: "2026-08-19T08:00:00.000Z", companyId: 1 },
  [HOOK_EVENTS.user.OFFLINE]: { user: USUARIO, at: "2026-08-19T18:00:00.000Z", companyId: 1 },

  // ─── Plataforma ───
  [HOOK_EVENTS.company.CREATED]: { company: EMPRESA },
  [HOOK_EVENTS.company.UPDATED]: {
    company: EMPRESA,
    changes: { name: { from: "Empresa Nova", to: "Empresa Nova Ltda" } }
  },
  [HOOK_EVENTS.plugin.INSTALLED]: { plugin: PLUGIN_INSTALACAO, companyId: 1 },
  [HOOK_EVENTS.plugin.UPDATED]: {
    plugin: PLUGIN_INSTALACAO,
    companyId: 1,
    changes: { baseUrl: { de: "https://antigo.exemplo.com.br/1", para: PLUGIN_INSTALACAO.baseUrl } }
  },
  [HOOK_EVENTS.plugin.REMOVED]: { plugin: PLUGIN_INSTALACAO, companyId: 1 },
  [HOOK_EVENTS.plugin.OFFLINE]: { plugin: PLUGIN_INSTALACAO, companyId: 1, reason: "HTTP sem resposta" },
  [HOOK_EVENTS.plugin.ONLINE]: { plugin: PLUGIN_INSTALACAO, companyId: 1 }
};

/** Instalação de plugin como aparece no webhook (sem credencial). */


/**
 * O exemplo de um evento — do catálogo do core ou declarado pelo plugin.
 *
 * O do plugin vence: ele é quem sabe o que manda. Sem exemplo nenhum, devolve
 * `undefined` e a tela mostra o formato genérico, que ao menos ensina o
 * envelope.
 */
export function sampleOf(evento?: EventDefinition): Record<string, any> | undefined {
  if (!evento) return undefined;
  // O cast é honesto: `name` é string porque um PLUGIN declara evento próprio,
  // fora do union do core. Nome de fora simplesmente não acha e cai no
  // undefined, que é o comportamento desejado.
  return evento.sample || EVENT_SAMPLES[evento.name as HookEventName];
}
